import React, {useEffect, useMemo, useState} from "react";
import {useGlobalFilter, usePagination, useSortBy, useTable} from "react-table";
import {
    Button,
    Flex,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue,
    useDisclosure,
    useToast,
    Select,
    Checkbox,
    Stack,
    Box,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import {
    tableColumns,
    TagActions,
    TagAmount, TagCount,
    TagDate,
    TagName, TagStatus
} from "./reportsTableData";

import { prettifyPayPeriodDate} from "../../../../functions/dateUtils";
import AdminReportDetailsModal from "./AdminReportDetailsModal";
import AdminNewReportModal from "./AdminNewReportModal";
import EndOfMonthSummaryModal from "./EndOfMonthSummaryModal";
import ReportsApi, {Reports, ReportState} from "../../../../api/reports";
import MonthDropdownSelection from "../../../../functions/MonthDropdownSelection";
import User from "../../../../api/user";

export default function AdminReportsTable() {
    const columns = useMemo(() => tableColumns, [tableColumns]);
    const [data, setData] = React.useState([]);
    const [filteredData, setFilteredData] = React.useState([]);
    const [date, setDate] = React.useState("");
    const [selectedReport, setSelectedReport] = React.useState(undefined);
    const [isApprovingAll, setIsApprovingAll] = React.useState(false);
    const [statusFilters, setStatusFilters] = useState({
        [ReportState.Created]: false,
        [ReportState.AdminApproved]: false,
        [ReportState.DocumentsSent]: false,
        [ReportState.DocumentsReceived]: false,
        [ReportState.SemiPaid]: false,
        [ReportState.FullyPaid]: false,
    });

    const refreshApplications = (dateOverride) => {
        const target = dateOverride || date;
        ReportsApi.getReports(target).then(res => {
            setData(res.data);
            applyFilters(res.data, statusFilters);
        })
            .catch(e => {
                setData([]);
                setFilteredData([]);
            });
    };

    const getMonthValue = (monthsBack) => {
        const now = new Date();
        const d = new Date(now.getFullYear(), now.getMonth() - monthsBack, 1);
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        return `${month}/${d.getFullYear()}`;
    };

    useEffect(() => {
        const findLatestMonthWithData = async () => {
            for (let i = 0; i < 12; i++) {
                const monthValue = getMonthValue(i);
                try {
                    const res = await ReportsApi.getReports(monthValue);
                    if (res.data && res.data.length > 0) {
                        setDate(monthValue);
                        setData(res.data);
                        applyFilters(res.data, statusFilters);
                        return;
                    }
                } catch (e) {
                    // continue to previous month
                }
            }
        };
        findLatestMonthWithData();
    }, []);

    // Apply status filters to the data
    const applyFilters = (dataToFilter, filters) => {
        // Check if any filter is active
        const anyFilterActive = Object.values(filters).some(value => value);

        if (!anyFilterActive) {
            // If no filters are active, show all data
            setFilteredData(dataToFilter);
            return;
        }

        // Filter data based on selected statuses
        const filtered = dataToFilter.filter(item =>
            filters[item.state] === true
        );

        setFilteredData(filtered);
    };

    // Handle status filter change
    const handleStatusFilterChange = (status) => {
        const newFilters = {
            ...statusFilters,
            [status]: !statusFilters[status]
        };

        setStatusFilters(newFilters);
        applyFilters(data, newFilters);
    };

    // Use filtered data for the table
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable({ columns, data: filteredData }, useSortBy);

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

    const {
        isOpen: isNewReportModalOpen,
        onOpen: onNewReportModalOpen,
        onClose: onNewReportModalClose
    } = useDisclosure();

    const {
        isOpen: isReportModalOpen,
        onOpen: onReportModalOpen,
        onClose: onReportModalClose
    } = useDisclosure();

    const {
        isOpen: isSummaryModalOpen,
        onOpen: onSummaryModalOpen,
        onClose: onSummaryModalClose
    } = useDisclosure();

    const handleRefreshClick = () => {
        refreshApplications();
    };

    const handleDateChange = (event) => {
        setDate(event);
    };

    const handleEditClick = (cellRow) => {
        setSelectedReport(filteredData[cellRow.index]);
        onReportModalOpen();
    };

    const handleNewReportClick = () => {
        onNewReportModalOpen();
    };

    const handleSummaryReportClick = () => {
        onSummaryModalOpen();
    };

    const handleApproveAllCreatedClick = async () => {
        const createdReports = data.filter(r => r.state === ReportState.Created);
        if (createdReports.length === 0) return;
        setIsApprovingAll(true);
        try {
            await Promise.all(createdReports.map(r => ReportsApi.updateReport(r.id, null, ReportState.AdminApproved)));
            refreshApplications();
        } finally {
            setIsApprovingAll(false);
        }
    };

    const toast = useToast();
    const [isDownloadingInvoices, setIsDownloadingInvoices] = useState(false);
    const userInfo = User.GetInfo();
    const isSuperAdmin = !!(userInfo && userInfo.screens && userInfo.screens.includes('tools'));
    // Xtras (companyId 43) runs a 26→25 pay cycle. Only type-2 (company) admins should see
    // the subtitle — a Payro superadmin impersonating or viewing globally keeps calendar labels.
    const isXtrasAdmin = !!(userInfo && Number(userInfo.type) === 2 && Number(userInfo.companyId) === 43);

    const handleDownloadMonthlyInvoicesClick = async () => {
        if (!date) return;
        setIsDownloadingInvoices(true);
        try {
            const res = await ReportsApi.downloadMonthlyInvoicesXlsx(date);
            const base64String = new TextDecoder().decode(res.data);
            const byteCharacters = atob(base64String);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            const [mm, yyyy] = date.split('/');
            const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const monthName = monthNames[parseInt(mm, 10) - 1];
            const shortYear = yyyy.slice(2);
            link.href = url;
            link.download = `Payro ${monthName} ${shortYear} invoices.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            toast({
                title: 'שגיאה בהורדת דו"ח חשבוניות חודשי',
                description: e?.response?.status === 404 ? 'לא נמצאו דוחות עם דראפטים לחודש זה' : undefined,
                position: 'top',
                status: 'error',
                isClosable: true,
            });
        } finally {
            setIsDownloadingInvoices(false);
        }
    };

    // Status filter section component
    const StatusFilterSection = () => (
        <Box mb='15px' p='15px' borderWidth='1px' borderRadius='md' borderColor={borderColor}>
            <Text mb='10px' fontWeight='bold'>סינון לפי סטטוס:</Text>
            <Stack direction='row' spacing={5}>
                <Checkbox
                    isChecked={statusFilters[ReportState.Created]}
                    onChange={() => handleStatusFilterChange(ReportState.Created)}
                >
                    נוצר
                </Checkbox>
                <Checkbox
                    isChecked={statusFilters[ReportState.AdminApproved]}
                    onChange={() => handleStatusFilterChange(ReportState.AdminApproved)}
                >
                    אושר
                </Checkbox>
                <Checkbox
                    isChecked={statusFilters[ReportState.DocumentsSent]}
                    onChange={() => handleStatusFilterChange(ReportState.DocumentsSent)}
                >
                    מסמכים נשלחו
                </Checkbox>
                <Checkbox
                    isChecked={statusFilters[ReportState.DocumentsReceived]}
                    onChange={() => handleStatusFilterChange(ReportState.DocumentsReceived)}
                >
                    מסמכים התקבלו
                </Checkbox>
                <Checkbox
                    isChecked={statusFilters[ReportState.SemiPaid]}
                    onChange={() => handleStatusFilterChange(ReportState.SemiPaid)}
                >
                    שולם חלקי
                </Checkbox>
                <Checkbox
                    isChecked={statusFilters[ReportState.FullyPaid]}
                    onChange={() => handleStatusFilterChange(ReportState.FullyPaid)}
                >
                    שולם
                </Checkbox>
            </Stack>
        </Box>
    );

    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            overflowX={{ sm: "scroll", lg: "hidden" }}>
            <Flex px='25px' mb='20px' align='center'>

                <MonthDropdownSelection handleSelectedMonth={handleDateChange} value={date} showXtrasSubtitle={isXtrasAdmin}/>

                <AdminReportDetailsModal date={date} report={selectedReport} isOpen={isReportModalOpen} onClose={onReportModalClose} />

                <AdminNewReportModal isOpen={isNewReportModalOpen} onClose={onNewReportModalClose} />

                <EndOfMonthSummaryModal isOpen={isSummaryModalOpen} onClose={onSummaryModalClose} />

                <Button marginRight='30px' width='80px' variant={'brand'} onClick={handleRefreshClick} >הצג</Button>

                <Button marginRight='30px' width='80px' variant={'brand'} onClick={handleNewReportClick}>חדש</Button>

                <Button
                    marginRight='30px'
                    width='160px'
                    variant={'brand'}
                    onClick={handleApproveAllCreatedClick}
                    isLoading={isApprovingAll}
                    loadingText='מאשר...'
                    isDisabled={data.filter(r => r.state === ReportState.Created).length === 0}
                >
                    אשר כל "נוצר"
                </Button>

                <Button marginRight='30px' width='120px' variant={'brand'} onClick={handleSummaryReportClick}>
                    דוח סיכום
                </Button>

                {isSuperAdmin && (
                    <Button
                        marginRight='30px'
                        width='180px'
                        variant={'brand'}
                        onClick={handleDownloadMonthlyInvoicesClick}
                        isLoading={isDownloadingInvoices}
                        loadingText='מייצר...'
                        isDisabled={!date}
                    >
                        חשבוניות חודשי (Excel)
                    </Button>
                )}

            </Flex>

            {/* Add the status filter section here */}
            <Box px='25px'>
                <StatusFilterSection />
            </Box>

            <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px'>
                <Thead>
                    {headerGroups.map((headerGroup, index) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    pe='10px'
                                    key={index}
                                    borderColor={borderColor}>
                                    <Flex
                                        justify='space-between'
                                        align='center'
                                        fontSize={{ sm: "10px", lg: "12px" }}
                                        color='gray.400'>
                                        {column.render("Header")}
                                    </Flex>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {rows.map((row, index) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()} key={index}>
                                {row.cells.map((cell, index) => {
                                    let data = "";
                                    let cellWidth = cell.column.width;
                                    if(cell.column.tag === TagAmount) {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                ₪{cell.value}
                                            </Text>
                                        );
                                    }
                                    else if (cell.column.tag === TagName || cell.column.tag === TagCount) {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {cell.value}
                                            </Text>
                                        );
                                    }
                                    else if(cell.column.tag === TagDate) {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {prettifyPayPeriodDate(cell.value)}
                                            </Text>
                                        );
                                    }
                                    else if(cell.column.tag === TagStatus) {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {Reports.stateText(cell.value)}
                                            </Text>
                                        );
                                    }
                                    else if (cell.column.tag === TagActions) {
                                        data = (
                                            <Flex align='center'>
                                                <Button onClick={() => handleEditClick(cell.row)} marginLeft='20px' textColor='black' backgroundColor='green.200' height='35px'>פרטים</Button>
                                            </Flex>
                                        );
                                    }
                                    return (
                                        <Td
                                            {...cell.getCellProps()}
                                            key={index}
                                            fontSize={{ sm: "14px" }}
                                            width={cellWidth}
                                            borderColor='transparent'>
                                            {data}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
        </Card>
    );
}
